/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Panel from '$components/Panel';
import { ErrorType } from '$variables/types';

import StudentsTable from './components/StudentsTable';
import ExportButtons from './components/ExportButtons';
import ExportPanelError from './components/ExportPanelError';
import { studentGradesType } from './types';
import { DEFAULT_VALUE, DO_NOT_EXPORT } from '$utils/overrideConstants';
import overrideOptions from '$utils/overrideOptions';
import hasGrade from '$utils/hasGrade';
import notEmptyArray from './utils/notEmptyArray';

import { Container, Loader, Centered } from './ExportPanel.styled';

const ExportPanel = ({
  open,
  onClose,
  onExportToSRS,
  onDownloadCsv,
  studentGrades,
  assessmentItemId,
  fetchGradesForAssignments,
  fetchCalculatedGrades,
  mappedAssignments,
  isInitiatingExport,
  isGeneratingCsv,
  isFetchingGrades,
  fetchGradesError,
  launchTurnitinAssignment,
  hasGradeOverride,
}) => {
  const { ssAstudentIdShowInCsVandSubList } = useFlags();
  const [localStudentGrades, setLocalStudentGrades] = useState([]);

  const getLocalOverrides = () => {
    const overrides = localStudentGrades.map(
      ({ studentId, selectedOverrideValue }) => ({
        studentId,
        code: selectedOverrideValue,
      })
    );

    return overrides;
  };

  const fetchGrades = () => {
    if (assessmentItemId) {
      const assignmentIds = mappedAssignments.map(({ id }) => id);

      fetchGradesForAssignments({ assignmentIds });
      fetchCalculatedGrades({ assessmentItemId });
    }
  };

  const setOverrides = () => {
    if (notEmptyArray(studentGrades)) {
      if (!localStudentGrades.length) {
        setLocalStudentGrades(studentGrades);
        return;
      }

      // since events can update student grades in background
      // we need to prioritize local overrides over state overrides
      const localOverrides = getLocalOverrides();

      const studentGradesWithLocalOverrides = studentGrades.map(
        (studentGrade) => {
          // studentOverride format: { studentId, code }
          const studentOverride = localOverrides.find(
            ({ studentId }) => studentId === studentGrade.studentId
          );

          if (!studentOverride) {
            return studentGrade;
          }

          // student's grade is removed
          // either by educator, or by student's re-submit
          // only triggered by event
          if (
            studentOverride.code === DEFAULT_VALUE &&
            !hasGrade(studentGrade)
          ) {
            return {
              ...studentGrade,
              selectedOverrideValue: DO_NOT_EXPORT,
            };
          }

          return {
            ...studentGrade,
            selectedOverrideValue: studentOverride.code,
          };
        }
      );

      setLocalStudentGrades(studentGradesWithLocalOverrides);
    }
  };

  const updateStudentOverride = (studentId) => (_, value) => {
    if (notEmptyArray(localStudentGrades)) {
      const newStudentGrades = localStudentGrades.map((studentGrade) => {
        if (studentGrade.studentId !== studentId) {
          return studentGrade;
        }

        return {
          ...studentGrade,
          selectedOverrideValue: value,
        };
      });

      setLocalStudentGrades(newStudentGrades);
    }
  };

  const handleExportToSRS = () => {
    // remove default value before saving overrides to DB
    const overrides = getLocalOverrides().filter(
      ({ code }) => code !== DEFAULT_VALUE
    );

    onExportToSRS(overrides);
  };

  const handleCSVDownload = () => {
    // remove default value before generating CSV
    const overrides = getLocalOverrides().filter(
      ({ code }) => code !== DEFAULT_VALUE
    );

    onDownloadCsv({
      assessmentItemId,
      overrides,
      overrideOptions,
      includeStudentId: ssAstudentIdShowInCsVandSubList,
    });
  };

  useEffect(fetchGrades, [assessmentItemId]);
  useEffect(setOverrides, [studentGrades]);

  const getPanelContent = () => {
    if (isFetchingGrades) {
      return (
        <Centered>
          <Loader aria-label="loading" />
        </Centered>
      );
    }

    if (fetchGradesError) {
      return <ExportPanelError onTryAgain={fetchGrades} />;
    }

    if (!localStudentGrades.length) {
      return null;
    }

    const totalStudents = localStudentGrades.length;

    const totalExportingStudents = localStudentGrades.filter(
      ({ selectedOverrideValue }) => selectedOverrideValue !== DO_NOT_EXPORT
    ).length;

    return (
      <>
        <ExportButtons
          totalStudents={totalStudents}
          studentsWithGrades={totalExportingStudents}
          onExportToSRS={handleExportToSRS}
          onCSVDownload={handleCSVDownload}
          isInitiatingExport={isInitiatingExport}
          isGeneratingCsv={isGeneratingCsv}
        />
        <StudentsTable
          studentGrades={localStudentGrades}
          assignments={mappedAssignments}
          updateStudentOverride={updateStudentOverride}
          launchTurnitinAssignment={launchTurnitinAssignment}
          hasGradeOverride={hasGradeOverride}
        />
      </>
    );
  };

  return (
    <Panel
      open={open}
      title="Export grades"
      subTitle="Export grades to the student record system or download a CSV file"
      onClose={onClose}
    >
      <Container>{getPanelContent()}</Container>
    </Panel>
  );
};

ExportPanel.propTypes = {
  // own props
  assessmentItemId: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onExportToSRS: PropTypes.func.isRequired,
  // merged props
  studentGrades: PropTypes.arrayOf(studentGradesType),
  // state props
  mappedAssignments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  fetchGradesForAssignments: PropTypes.func.isRequired,
  fetchCalculatedGrades: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired,
  isInitiatingExport: PropTypes.bool,
  isGeneratingCsv: PropTypes.bool,
  isFetchingGrades: PropTypes.bool,
  fetchGradesError: ErrorType,
  launchTurnitinAssignment: PropTypes.func.isRequired,
  hasGradeOverride: PropTypes.bool,
};

ExportPanel.defaultProps = {
  assessmentItemId: null,
  open: false,
  studentGrades: [],
  isInitiatingExport: false,
  isGeneratingCsv: false,
  isFetchingGrades: false,
  fetchGradesError: null,
  hasGradeOverride: false,
};

export default ExportPanel;
